import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, MessageCircle, LayoutGrid, Bot, Settings as SettingsIcon, LogOut, Menu, X, RefreshCw } from "lucide-react";

const NAV = [
  { key: "today", label: "Today", path: "/demo/today", icon: Zap, adminOnly: false },
  { key: "inbox", label: "Inbox", path: "/demo/inbox", icon: MessageCircle, adminOnly: false },
  { key: "crm", label: "Pipeline", path: "/demo/crm", icon: LayoutGrid, adminOnly: false },
  { key: "automation", label: "Automation", path: "/demo/automation", icon: Bot, adminOnly: true },
  { key: "settings", label: "Settings", path: "/demo/settings", icon: SettingsIcon, adminOnly: true },
];

function relTime(d) {
  if (!d) return "—";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

export default function DemoTopbar({ auth, handleSignOut, lastSyncedAt, onRefresh, error, unreadCount }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const isAdmin = auth?.username === "admin";
  const items = NAV.filter(n => !n.adminOnly || isAdmin);
  const activeKey = items.find(n => location.pathname.startsWith(n.path))?.key;
  const activeLabel = NAV.find(n => n.key === activeKey)?.label || "";

  // Live status pill
  const ageSec = lastSyncedAt ? Math.floor((Date.now() - new Date(lastSyncedAt).getTime()) / 1000) : null;
  const stale = ageSec != null && ageSec > 60;
  const dotColor = error ? "#ef4444" : stale ? "#f59e0b" : "#10b981";
  const liveLabel = error ? "Sync error" : stale ? `stale ${relTime(lastSyncedAt)}` : `live · ${relTime(lastSyncedAt)}`;

  return (
    <header className="dt-shell" data-testid="demo-topbar">
      <div className="dt-left">
        <button className="dt-burger" onClick={() => setMobileOpen(!mobileOpen)} data-testid="dt-burger" aria-label="Menu">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <span className="dt-brand" onClick={() => navigate("/demo/today")} role="link">
          BulkJobApply <span className="dt-brand-sub">OS</span>
        </span>
        {activeLabel && <span className="dt-pagetitle" data-testid="dt-page-title">{activeLabel}</span>}
      </div>

      <nav className={`dt-nav ${mobileOpen ? "dt-nav-open" : ""}`}>
        {items.map(item => {
          const Icon = item.icon;
          const active = activeKey === item.key;
          return (
            <button
              key={item.key}
              className={`dt-nav-btn ${active ? "dt-nav-active" : ""}`}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              data-testid={`dt-nav-${item.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{item.label}</span>
              {item.key === "inbox" && unreadCount > 0 && (
                <span className="dt-nav-badge" data-testid="dt-nav-inbox-unread">{unreadCount}</span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="dt-right">
        <button
          className="dt-live-pill"
          onClick={onRefresh}
          title={error ? `Last error: ${error}` : "Click to refresh"}
          data-testid="dt-live-pill"
        >
          <span className="dt-live-dot" style={{ background: dotColor }} />
          <span className="dt-live-label">{liveLabel}</span>
          <RefreshCw className="w-3 h-3 dt-live-refresh" />
        </button>
        <div className="dt-user" data-testid="dt-user">
          <span className="dt-user-name">{auth?.name || auth?.username}</span>
          <span className="dt-user-role">{isAdmin ? "Admin" : "Closer"}</span>
        </div>
        <button className="dt-signout" onClick={handleSignOut} title="Sign out" data-testid="dt-signout">
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </header>
  );
}
